import { createRouter, createWebHistory } from 'vue-router';
import DashboardView from '@/views/dashboard/DashboardView.vue';

import { useAuthStore } from '@/store/global/auth';
import { useRecommendationsStore } from '@/store/tasks/recommendations';
import { useMaintenanceStore } from '@/store/global_settings/maintenanceBanner';
import { useRecommendationDetailStore } from '@/store/tasks/recommendation-detail';
import { usedevLogsStore } from '@/store/global_settings/devLogs';
import { useProfileStore } from '@/store/global/profile.js';
import { useBetaStore } from '@/store/global_settings/beta';
import { useMarketStore } from '@/store/global/market';
import { storeToRefs } from 'pinia';
import { useArticlesStore } from '@/store/text-editor/articles';

const router = createRouter({
	history: createWebHistory(import.meta.env.BASE_URL),
	routes: [
		{
			path: '/',
			name: 'dashboard',
			meta: {
				isAuth: true,
			},
			component: DashboardView,
		},
		{
			path: '/seo-analysis',
			name: 'seo-analysis',
			meta: {
				isAuth: true,
			},
			component: DashboardView,
		},
        {
			path: '/geo-analysis',
			name: 'geo-analysis',
			meta: {
				isAuth: true,
			},
			component: DashboardView,
		},
		{
			path: '/team-management',
			name: 'team-management',
			meta: {
				isAuth: true,
			},
			component: DashboardView,
		},
		{
			path: '/edit-profile',
			name: 'edit-profile',
			meta: {
				isAuth: true,
			},
			component: () => import('@/views/edit_profile/EditProfileView.vue'),
		},
		{
			path: '/login',
			name: 'login',
			meta: {
				isAuth: false,
			},
			component: () => import('@/views/authentification/LoginView.vue'),
		},
		{
			path: '/oauth/google',
			name: 'oauth-google',
			meta: {
				isAuth: false,
			},
			component: () => import('@/views/oauth/GoogleView.vue'),
		},
		{
			path: '/oauth/microsoft',
			name: 'oauth-microsoft',
			meta: {
				isAuth: false,
			},
			component: () => import('@/views/oauth/MicrosoftView.vue'),
		},
		{
			path: "/oauth/jira",
			name: "oauth-jira",
			meta: {
				isAuth: false,
			},
			component: () => import("@/views/oauth/JiraView.vue"),
		},
		{
			path: "/oauth/monday",
			name: "oauth-monday",
			meta: {
				isAuth: false,
			},
			component: () => import("@/views/oauth/MondayView.vue"),
		},
		{
			path: "/oauth/linkedin",
			name: "oauth-linkedin",
			meta: {
				isAuth: false,
			},
			component: () => import("@/views/oauth/LinkedinView.vue"),
		},
		{
			path: "/content-guidance",
			name: "content-guidance",
			meta: {
				isAuth: true,
			},
			component: () => import('@/views/tasks/TasksView.vue'),
		},
		{
			path: '/tasks',
			name: 'tasks',
			meta: {
				isAuth: true,
			},
			component: () => import('@/views/tasks/TasksView.vue'),
			props: (route) => ({
				filters: route.query.filter
			})
		},
		{
			path: '/tasks/:id',
			name: 'task',
			meta: {
				isAuth: true,
				onMarketChange: 'tasks',
			},
			component: () => import('@/views/tasks/TasksView.vue'),
			beforeEnter: (to, from, next) => {
				handleMarketChange(to, next, 'task');
			},
		},
		{
			path: '/tasks_details/:id',
			name: 'tasks-level-1',
			meta: {
				isAuth: true,
				onMarketChange: 'tasks',
			},
			component: () => import('@/views/tasks/TasksLevel1View.vue'),
			beforeEnter: async (to, from, next) => {
				handleMarketChange(to, next, 'task');
				const id = parseInt(to.params.id);
				const recommendationDetailStore = useRecommendationDetailStore();

				try {
					await recommendationDetailStore.fetchData(id);
					const { recommendation } = storeToRefs(recommendationDetailStore);

					if (!recommendation.value.id) {
						// Redirect to the "not-found" route if recommendation is not found
						next({
							name: 'not-found',
							params: { pathMatch: to.path.substring(1).split('/') },
							query: to.query,
							hash: to.hash,
						});
					} else {
						// Continue to the intended route
						next();
					}
				} catch (error) {
					console.error(error);
					// Handle error and possibly redirect to an error page
					next({
						name: 'not-found', // Replace with the actual error route name
					});
				}
			},
		},
		// do no delete, it's for future use
		// {
		// 	path: "/task/:id",
		// 	name: "tasks-details",
		// 	meta: {
		// 		isAuth: true
		// 	},
		// 	component: () => import("@/views/tasks/TasksDetailsView.vue"),
		// 	beforeEnter: (to) => {

		// 		const id = parseInt(to.params.id);
		// 		const recommendationsStore = useRecommendationsStore();
		// 		const { tasks } = storeToRefs(recommendationsStore);
		// 		const task = tasks.value.find((task) => task.id === id);

		// 		if (!task) {
		// 			return {
		// 				name: 'not-found',
		// 				params: { pathMatch: to.path.substring(1).split('/') },
		// 				query: to.query,
		// 				hash: to.hash,
		// 			}
		// 		}
		// 	}
		// },
		{
			path: '/visibility',
			name: 'visibility',
			meta: {
				isAuth: true,
			},
			component: () => import('@/views/visibility/VisibilityView.vue'),
		},
        {
            path: '/visibility/generated-labels',
            name: 'generated-labels',
            meta: {
                isAuth: true,
            },
            component: () => import('@/views/visibility/VisibilityView.vue'),
        },
        {
            path: '/visibility/assigned-labels',
            name: 'assigned-labels',
            meta: {
                isAuth: true,
            },
            component: () => import('@/views/visibility/VisibilityView.vue'),
        },
		{
			path: '/calendar',
			name: 'calendar',
			meta: {
				isAuth: true,
			},
			component: () => import('@/views/calendar/CalendarView.vue'),
		},
		{
			path: '/finder',
			name: 'finder',
			meta: {
				isAuth: true,
			},
			component: () => import('@/views/finder/FinderView.vue'),
			props: (route) => ({
				searchParams: route.query
			})
		},
		{
			path: '/users',
			name: 'users',
			meta: {
				isAuth: true,
				access: 'project manager',
			},
			component: () => import('@/views/users/UsersView.vue'),
		},
		{
			path: '/clients',
			name: 'clients',
			meta: {
				isAuth: true,
				access: 'admin',
			},
			component: () => import('@/views/clients/ClientsView.vue'),
		},
		{
			path: '/project',
			name: 'project',
			meta: {
				isAuth: true,
			},
			component: () => import('@/views/project/ProjectView.vue'),
		},
		{
			path: '/integration',
			name: 'integration',
			meta: {
				isAuth: true,
			},
			component: () => import('@/views/settings/SettingsView.vue'),
		},
        {
			path: '/crawler',
			name: 'crawler',
			meta: {
				isAuth: true,
                access: 'super_admin',
			},
			component: () => import('@/views/crawler/CrawlerData.vue'),
		},
		{
			path: '/api',
			name: 'api',
			meta: {
				isAuth: true,
			},
			component: () => import('@/views/api/ApiView.vue'),
			props: (route) => ({
				filters: route.query.filter
			})
		},
		{
			path: '/references',
			name: 'references',
			meta: {
				isAuth: true,
			},
			component: () => import('@/views/references/ReferencesView.vue'),
		},
		{
			path: '/tips',
			name: 'tips',
			meta: {
				isAuth: false,
			},
			component: () => import('@/views/tips/TipsView.vue'),
		},
		{
			path: '/logout',
			name: 'logout',
			meta: {
				isAuth: false,
			},
			component: () => import('@/views/authentification/LogoutView.vue'),
            // component: () => import('@/views/authentification/LoginView.vue'),
		},
		{
			path: '/:pathMatch(.*)*',
			name: 'not-found',
			meta: {
				isAuth: true,
			},
			component: () => import('@/views/PageNotFound.vue'),
		},
		{
			path: '/settings/oauth/google',
			name: 'settings-oauth-google',
			meta: {
				isAuth: true,
			},
			component: () => import('@/views/oauth/SettingGoogleView.vue'),
		},
		{
			path: '/global-settings/',
			name: 'global-settings',
			meta: {
				isAuth: true,
				access: 'super_admin',
			},
			component: () => import('@/views/global-settings/GlobalSettingsView.vue'),
		},
		{
			path: '/keyword/:id',
			name: 'keyword-details',
			meta: {
				isAuth: true,
				onMarketChange: 'visibility',
			},
			component: () => import('@/views/visibility/VisibilityView.vue'),
			// component: () => import('@/views/keyword_details/KeywordDetailsView.vue'),
			beforeEnter: (to, from, next) => {
				handleMarketChange(to, next, 'keyword');
			},
		},
		{
			path: '/question/:id',
			name: 'question-details',
			meta: {
				isAuth: true,
				onMarketChange: 'visibility',
			},
			component: () => import('@/views/visibility/VisibilityView.vue'),
			// component: () => import('@/views/keyword_details/KeywordDetailsView.vue'),
			beforeEnter: (to, from, next) => {
				handleMarketChange(to, next, 'question');
			},
		},
		{
			path: '/resetPassword',
			name: 'reset-password',
			meta: {
				isAuth: false,
			},
			component: () => import('@/views/resetPassword/ResetPasswordView.vue'),
		},
		{
			path: '/createPassword',
			name: 'create-password',
			meta: {
				isAuth: false,
			},
			component: () => import('@/views/createPassword/CreatePasswordView.vue'),
		},
        {
			path: '/articles',
			name: 'articles-list',
			meta: {
				isAuth: true,
			},
			component: () => import('@/views/textEditor/ArticleListView.vue'),
		},
		{
			path: '/textEditor/:id',
			name: 'text-editor',
			meta: {
				isAuth: true,
				onMarketChange: 'articles-list',
			},
			component: () => import('@/views/textEditor/TextEditorView.vue'),
			beforeEnter: async (to, from, next) => {
				handleMarketChange(to, next, 'keyword');
				const id = parseInt(to.params.id);
				const articlesStore = useArticlesStore();

				try {
					await articlesStore.fetchArticle(id);
					const { article } = storeToRefs(articlesStore);

					if (!article.value || !article.value.id) {
						// Redirect to the "not-found" route if article is not found
						next({
							name: 'not-found',
							params: { pathMatch: to.path.substring(1).split('/') },
							query: to.query,
							hash: to.hash,
						});
					} else {
						// Continue to the intended route
						next();
					}
				} catch (error) {
					console.error(error);
					// Handle error and possibly redirect to an error page
					next({
						name: 'not-found', // Replace with the actual error route name
					});
				}
			},
		},
		{
			path: '/technical',
			name: 'technical',
			meta: {
				isAuth: true,
			},
			component: () => import('@/views/technical/TechnicalView.vue'),
		},
		{
			path: '/asa',
			name: 'auto-sem-analysis',
			meta: {
				isAuth: true,
			},
			component: () => import('@/views/auto-sem-analysis/AutoSemAnalysisView.vue'),
		},
		{
			path: '/billing',
			name: 'plan-management',
			meta: {
				isAuth: true,
                access: 'super_admin',
			},
			component: () => import('@/views/plan_management/PlanManagementView.vue'),
		},
		{
			path: '/api-test/',
			name: 'api-test',
			meta: {
				isAuth: true,
				access: 'super_admin',
			},
			component: () => import('@/views/api/RosselView.vue'),
		},
		{
			path: '/kickoff',
			name: 'kickoff',
			meta: {
				isAuth: false,
			},
			component: () => import('@/views/kick-off/kickOffView.vue'),
		},
		{
			path: '/log-analyzer',
			name: 'log-analyzer',
			meta: {
				isAuth: true,
				access: 'admin',
			},
			component: () => import('@/views/log_analyzer/LogAnalyzerView.vue'),
		}
	],
	linkActiveClass: 'active-link',
	linkExactActiveClass: 'exact-active-link',
});

router.beforeEach((to, from, next) => {
	const dev_logs = usedevLogsStore();

	const user = useAuthStore();
	const { access } = storeToRefs(user);

	const profile = useProfileStore();
	const { user_name, user_image } = storeToRefs(profile);

	const maintenanceStore = useMaintenanceStore();
	const { banners, closed, showed } = storeToRefs(maintenanceStore);

	const betaStore = useBetaStore();
	const { selectedProjectPlan } = storeToRefs(betaStore);

	const marketStore = useMarketStore();
	const { projectName, marketName, on_boarding } = storeToRefs(marketStore);

	if (to.meta.isAuth == true) {
		maintenanceStore.fetchData();
		dev_logs.fetchData();
	}

	showed.value.active = false;
	banners.value.forEach((banner, index) => {
		if (to.name == banner.maintenance_page) {
			if (closed.value[index].status == false) {
				showed.value = {
					active: banner.maintenance_status,
					message: banner.maintenance_cause,
					id: banner.id,
				};
			}
			else {
				showed.value = {
					active: false,
					message: banner.maintenance_cause,
				};
			}
		}
	});

	if (to.meta.isAuth && !user.isLoggedIn) {
		next('/login');
	}
	else if (to.path === '/logout') {
		next();
	}
    else if ((to.path === '/login' && user.isLoggedIn) || (to.path === '/kickoff' && on_boarding.value && !to.query.project_id)) {
        next('/');
    }
	else if (!on_boarding.value && to.path !== '/kickoff' && to.path !== '/logout') {

		if (user.hasAccess('super_admin') == true) {
			next();
		}

		if (selectedProjectPlan.value.plan == 'AGENCY') {
			next();
		}

		// next('/kickoff');
		next();
	}
	else {
		next();
	}

	if (to.meta.access) {
		if (user.hasAccess(to.meta.access) == false) {
			history.back();
		}
	}

	if (to.meta.isAuth && user.isLoggedIn) {
		const user_obj = user.getUser().then(user_obj => {
			const userDataLayer = {
				id: user_obj.id,
				name: user_name.value,
				access: access.value,
				email: user_obj.email,
				language: user_obj.language,
				plan: selectedProjectPlan.value?.plan,
				project: projectName.value,
				market: marketName.value
			};
			window.dataLayer.push({
				user: userDataLayer
			});
		});
	}
});

export default router;

function handleMarketChange(to, next, name) {
	const marketStore = useMarketStore()
    const id = parseInt(to.params.id);

	marketStore.forceMarketChange(id, name);
	next();
}